import React, { useEffect, useRef, useState, useCallback } from "react";
import { createGameState, updateGame, render, W, H } from "@/game/engine";
import { sfx, resumeAudio } from "@/lib/sfx";
import { setHighScore, getHighScore } from "@/lib/highscore";
import { getIsPro } from "@/lib/pro";
import { getHandleGem } from "@/lib/achievementsExtra";
import { hasDuelistTitle } from "@/lib/duel";
import { useT } from "@/i18n";
import TouchControls from "@/components/TouchControls";

const KEY_MAP = {
  ArrowUp: "up",
  ArrowDown: "down",
  ArrowLeft: "left",
  ArrowRight: "right",
  KeyW: "up",
  KeyS: "down",
  KeyA: "left",
  KeyD: "right",
};

export default function GameCanvas({ level, onExit, onGameOver, onWin, seed = null, skinColor = null, skinGlow = null, ghostReplay = null, onSample = null, extraLife = false, scoreMultiplier = 1, weather = null }) {
  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const inputRef = useRef({ up: false, down: false, left: false, right: false });
  const rafRef = useRef(0);
  const lastTsRef = useRef(0);
  const [, force] = useState(0);
  const [hud, setHud] = useState({ score: 0, time: 0, lives: 3, shield: 0, speed: 0, freeze: 0, stage: 1 });
  const [paused, setPaused] = useState(false);
  const [stageBanner, setStageBanner] = useState(null);
  const lastStageRef = useRef(1);

  const draw = useCallback(() => {
    const ctx = canvasRef.current.getContext("2d");
    render(ctx, stateRef.current);
  }, []);

  useEffect(() => {
    stateRef.current = createGameState(level, level === "underground" ? 2 : 1, { isPro: getIsPro(), seed, skinColor, skinGlow, extraLife, scoreMultiplier, weather, handleGem: getHandleGem(), duelist: hasDuelistTitle() });
    stateRef.current.ghostReplay = ghostReplay;
    lastStageRef.current = 1;
    resumeAudio();
    sfx.startAmbient(level);
    // Fog weather = dampened ambient (audio parity with visual vignette)
    if (weather && weather.type === "fog") sfx.setMuffled(true);
    else sfx.setMuffled(false);
    lastTsRef.current = performance.now();
    let footTimer = 0;
    let screechTimer = 2;

    const loop = (ts) => {
      const dt = Math.min(0.033, (ts - lastTsRef.current) / 1000);
      lastTsRef.current = ts;
      if (!paused) {
        const s = stateRef.current;
        const prevLives = s.lives;
        updateGame(s, dt, inputRef.current);

        // Record player position for daily-challenge ghost replay
        if (onSample) onSample(s.time, s.player.x, s.player.y, s.score);

        // Footsteps while moving
        const moving = inputRef.current.up || inputRef.current.down || inputRef.current.left || inputRef.current.right;
        footTimer -= dt;
        if (moving && footTimer <= 0) {
          sfx.step();
          const boosted = s.time * 1000 < s.player.speedBoostUntil;
          footTimer = boosted ? 0.18 : 0.28;
        }

        // Random tire-screech / horn from enemies when close
        screechTimer -= dt;
        if (screechTimer <= 0) {
          const p = s.player;
          const near = s.enemies.some((e) => Math.hypot(e.x - p.x, e.y - p.y) < 220);
          if (near) {
            if (Math.random() < 0.5) sfx.tireScreech();
            else sfx.hornHonk();
          }
          screechTimer = 1.4 + Math.random() * 1.8;
        }

        if (s.lives < prevLives) sfx.hit();
        if (s.over) {
          sfx.crash();
          sfx.stopAmbient();
          const isHigh = setHighScore(s.level, s.score);
          const snap = canvasRef.current ? canvasRef.current.toDataURL("image/png") : null;
          onGameOver({ score: s.score, time: s.time, level: s.level, isHigh, beatBoss: false, snapshot: snap });
          return;
        }
        if (s.won) {
          sfx.win();
          sfx.stopAmbient();
          const isHigh = setHighScore(s.level, s.score);
          const snap = canvasRef.current ? canvasRef.current.toDataURL("image/png") : null;
          onWin({ score: s.score, time: s.time, level: s.level, isHigh, beatBoss: !!s.bossSpawned, snapshot: snap });
          return;
        }
        // Stage-up banner + sound
        if (s.stage > lastStageRef.current) {
          lastStageRef.current = s.stage;
          setStageBanner({ stage: s.stage, at: Date.now() });
          sfx.pickup(); // celebratory chime
          setTimeout(() => setStageBanner(null), 1800);
        }
        // Update HUD approx every frame
        const nowMs = s.time * 1000;
        setHud({
          score: s.score,
          time: s.time,
          lives: s.lives,
          stage: s.stage,
          shield: Math.max(0, s.player.invincibleUntil - nowMs),
          speed: Math.max(0, s.player.speedBoostUntil - nowMs),
          freeze: Math.max(0, s.player.freezeUntil - nowMs),
        });
      }
      draw();
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    const onKeyDown = (e) => {
      if (e.code === "Escape") {
        setPaused((v) => !v);
        return;
      }
      const k = KEY_MAP[e.code];
      if (k) {
        inputRef.current[k] = true;
        e.preventDefault();
      }
    };
    const onKeyUp = (e) => {
      const k = KEY_MAP[e.code];
      if (k) inputRef.current[k] = false;
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      sfx.stopAmbient();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, paused]);

  const highs = getHighScore();
  const highScore = highs[level] || 0;
  const { t } = useT();

  return (
    <div className="relative w-full flex flex-col items-center" data-testid="game-container">
      {/* HUD Top */}
      <div className="w-full max-w-[960px] flex items-center justify-between px-2 py-3 text-xs uppercase tracking-[0.25em]">
        <div className="flex gap-6">
          <div data-testid="hud-score">
            <div className="text-white/40">{t("hud.score")}</div>
            <div className="text-cyan-300 text-2xl font-bold tabular-nums glow-cyan">
              {String(hud.score).padStart(6, "0")}
            </div>
          </div>
          <div data-testid="hud-time">
            <div className="text-white/40">{t("hud.time")}</div>
            <div className="text-white text-2xl font-bold tabular-nums">
              {hud.time.toFixed(1)}s
            </div>
          </div>
          <div data-testid="hud-stage">
            <div className="text-white/40">{t("hud.stage")}</div>
            <div className="text-[#FFD400] text-2xl font-bold tabular-nums" style={{ textShadow: "0 0 12px rgba(255,212,0,0.6)" }}>
              {String(hud.stage).padStart(2, "0")}
            </div>
          </div>
          <div>
            <div className="text-white/40">{t("hud.hi")} · {level.toUpperCase()}</div>
            <div className="text-fuchsia-400 text-xl font-bold tabular-nums glow-magenta">
              {String(highScore).padStart(6, "0")}
            </div>
          </div>
        </div>
        <div className="flex gap-4 items-end">
          <div data-testid="hud-lives">
            <div className="text-white/40 text-right">{t("hud.lives")}</div>
            <div className="flex gap-1 mt-1">
              {Array.from({ length: Math.max(3, hud.lives) }).map((_, i) => (
                <div
                  key={i}
                  className={`w-3 h-3 border ${i < hud.lives ? "bg-[#FF0055] border-[#FF0055]" : "bg-transparent border-white/20"}`}
                  style={{ boxShadow: i < hud.lives ? "0 0 10px rgba(255,0,85,0.6)" : "none" }}
                />
              ))}
            </div>
          </div>
          <button
            data-testid="pause-button"
            onClick={() => {
              sfx.click();
              setPaused((v) => !v);
            }}
            className="text-[10px] tracking-[0.3em] px-3 py-2 border border-white/20 hover:border-cyan-300 hover:text-cyan-300 transition-colors"
          >
            {paused ? t("hud.resume") : t("hud.pause")}
          </button>
          <button
            data-testid="quit-button"
            onClick={() => {
              sfx.click();
              onExit();
            }}
            className="text-[10px] tracking-[0.3em] px-3 py-2 border border-white/20 hover:border-[#FF0055] hover:text-[#FF0055] transition-colors"
          >
            {t("hud.quit")}
          </button>
        </div>
      </div>

      {/* Power-up indicators */}
      <div className="w-full max-w-[960px] flex gap-3 px-2 pb-2 text-[10px] tracking-[0.25em]" data-testid="hud-powerups">
        <PowerBadge label="SHIELD" ms={hud.shield} color="#39FF14" />
        <PowerBadge label="BOOST" ms={hud.speed} color="#00F0FF" />
        <PowerBadge label="FREEZE" ms={hud.freeze} color="#FFD400" />
      </div>

      <div className="relative border-glow-cyan bg-black scanline" style={{ width: W, maxWidth: "100%" }}>
        <canvas
          ref={canvasRef}
          width={W}
          height={H}
          data-testid="game-canvas"
          className="block w-full h-auto"
          style={{ imageRendering: "pixelated" }}
        />
        {paused && (
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center gap-4" data-testid="pause-overlay">
            <div className="font-display text-5xl tracking-tighter glow-cyan">{t("hud.paused")}</div>
            <div className="text-xs tracking-[0.3em] text-white/60">{t("hud.press_esc")}</div>
          </div>
        )}
        {stageBanner && (
          <div
            key={stageBanner.at}
            className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none drift-in"
            data-testid="stage-banner"
          >
            <div className="text-[10px] tracking-[0.5em] text-[#FFD400]/80">{t("hud.difficulty_up")}</div>
            <div
              className="font-display font-black text-7xl tracking-tighter text-[#FFD400]"
              style={{ textShadow: "0 0 32px rgba(255,212,0,0.8)" }}
            >
              {t("hud.stage")} {String(stageBanner.stage).padStart(2, "0")}
            </div>
            <div className="mt-2 text-xs tracking-[0.3em] text-white/80">{t("hud.stage_desc")}</div>
          </div>
        )}
      </div>

      <div className="mt-3 text-[10px] tracking-[0.3em] text-white/40">
        {t("hud.controls_hint")} <span className="text-[#39FF14]">{t("hud.green_exit")}</span>
      </div>
      <TouchControls inputRef={inputRef} onTogglePause={() => { sfx.click(); setPaused((v) => !v); }} />
    </div>
  );
}

function PowerBadge({ label, ms, color }) {
  const active = ms > 0;
  return (
    <div
      className={`px-2 py-1 border ${active ? "" : "opacity-30"}`}
      style={{
        borderColor: active ? color : "rgba(255,255,255,0.15)",
        color: active ? color : "rgba(255,255,255,0.5)",
        boxShadow: active ? `0 0 16px ${color}66` : "none",
      }}
    >
      {label} {active ? `· ${(ms / 1000).toFixed(1)}S` : ""}
    </div>
  );
}
